import * as GC from '@grapecity/spread-sheets';
import { SpreadLookupInfo } from '@models/lookup-data';

//
// Styling helpers for Spread cells
//

const FONTSIZE = 10;
const FONTNAME = 'Source Sans Pro';

const COLORS = {
  // * ┌──────────────────────────┐
  // * │ mnemonicBackground       │
  // * ├──────────────────────────┼────────────────────────┬────────────────────────┐
  // * │ thirdDimensionBackground │ rowBackground          │ rowBackground          │
  // * ├──────────────────────────┼────────────────────────┼────────────────────────┤
  // * │ columnBackground         │ resultsBackground      │ resultsBackground      │
  // * │ columnLightBackground    │ resultsLightBackground │ resultsLightBackground │
  // * └──────────────────────────┴────────────────────────┴────────────────────────┘
  // *

  // columnBackground: 'rgb(155,187,89)',
  // columnLightBackground: 'rgb(165,194,105)',
  // resultsBackground: 'rgb(238,236,225)',
  // resultsLightBackground: 'rgb(242,241,233)',
  // newThirdDimensionBackground: 'rgb(142,204,219)',
  // subtleBorders: 'rgba(0,0,0,0.03)',
  // rowBackground: 'rgb(226,107,10)',
  // thirdDimensionBackground: 'rgb(75,172,198)',
  // mnemonicBackground: 'yellow',
  // flash: 'rgb(255,128,128)',

  columnBackground: 'rgb(102,139,166)',
  columnLightBackground: 'rgb(129,159,182)',
  resultsBackground: 'rgb(234,234,234)',
  resultsLightBackground: 'rgb(255,255,255)',
  newThirdDimensionBackground: 'rgb(142,204,219)',
  subtleBorders: 'rgba(0,0,0,0.03)',
  rowBackground: 'rgb(129,159,182)',
  thirdDimensionBackground: 'rgb(234,234,234)',
  mnemonicBackground: 'rgb(226,239,218)',
  flash: 'rgb(255,128,128)',

  balanceStructure: {
    gray: '#A0A0A0',
  },
};

const FLASH = {
  duration: 1000,
  interval: 250,
};

export const addBlackBorder = (range: GC.Spread.Sheets.CellRange) =>
  range.setBorder(new GC.Spread.Sheets.LineBorder('#000000', GC.Spread.Sheets.LineStyle.thin), {
    all: false,
    outline: true,
  });
export const addGrayBorder = (range: GC.Spread.Sheets.CellRange) =>
  range.setBorder(new GC.Spread.Sheets.LineBorder('#808080', GC.Spread.Sheets.LineStyle.thin), {
    all: false,
    outline: true,
  });

export const addRedBorder = (range: GC.Spread.Sheets.CellRange) =>
  range.setBorder(new GC.Spread.Sheets.LineBorder('#FF0000', GC.Spread.Sheets.LineStyle.thin), {
    all: false,
    outline: true,
  });

export const clearBorders = (range: GC.Spread.Sheets.CellRange) =>
  range.setBorder(new GC.Spread.Sheets.LineBorder('#000000', GC.Spread.Sheets.LineStyle.empty), {
    all: false,
    outline: true,
  });

export const setNonCategoryStyle = (range: GC.Spread.Sheets.CellRange) =>
  range.font(`11pt ${FONTNAME}`).foreColor(COLORS.balanceStructure.gray);
export const setCategoryStyle = (range: GC.Spread.Sheets.CellRange) => range.font(`11pt ${FONTNAME}`).foreColor(`black`);
export const setRootCategoryStyle = (range: GC.Spread.Sheets.CellRange) => range.font(`bold 11pt ${FONTNAME}`).foreColor(`black`);

export const setMnemonicBackground = (range: GC.Spread.Sheets.CellRange) =>
  range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .cellPadding(`0 ${FONTSIZE}`)
    .font(`bold ${FONTSIZE + 2}pt ${FONTNAME}`)
    .textDecoration(null)
    .backColor(COLORS.mnemonicBackground);

export const setThirdDimensionStyle = (range: GC.Spread.Sheets.CellRange) =>
  range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .cellPadding(`0 ${FONTSIZE}`)
    .font(`bold ${FONTSIZE}pt ${FONTNAME}`)
    .textDecoration(null)
    .backColor(COLORS.thirdDimensionBackground);

export const setNewThirdDimensionStyle = (range: GC.Spread.Sheets.CellRange) =>
  range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .height(25)
    .font(`bold ${FONTSIZE}pt ${FONTNAME}`)
    .backColor(COLORS.newThirdDimensionBackground);

export const setRowBackground = (range: GC.Spread.Sheets.CellRange, sheet: GC.Spread.Sheets.Worksheet, toExport: boolean = false) => {
  range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
    .cellPadding(`0 ${FONTSIZE}`)
    .font(`bold ${FONTSIZE}pt ${FONTNAME}`)
    .backColor(COLORS.rowBackground);

  if (!toExport) {
    range.setBorder(new GC.Spread.Sheets.LineBorder(COLORS.subtleBorders, GC.Spread.Sheets.LineStyle.thin), {
      all: true,
      outline: false,
    });
  }
  return range;
};

export const setColumnBackground = (range: GC.Spread.Sheets.CellRange, sheet: GC.Spread.Sheets.Worksheet, toExport: boolean = false) => {
  range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
    .textDecoration(null)
    .cellPadding(`0 ${FONTSIZE}`)
    .font(`bold ${FONTSIZE}pt ${FONTNAME}`)
    .backColor(COLORS.columnBackground);

  const { row, rowCount, col, colCount } = range;
  if (rowCount > 2) {
    for (let r = row; r < row + rowCount; r++) {
      if ((row - r) % 2 === 0) {
        const line = new GC.Spread.Sheets.CellRange(sheet, r, col, 1, colCount);
        line.backColor(COLORS.columnLightBackground);
      }
    }
  }

  if (!toExport) {
    range.setBorder(new GC.Spread.Sheets.LineBorder(COLORS.subtleBorders, GC.Spread.Sheets.LineStyle.thin), {
      all: true,
      outline: false,
    });
  }
  return range;
};

export const setResultsBackground = (range: GC.Spread.Sheets.CellRange, sheet: GC.Spread.Sheets.Worksheet, toExport: boolean = false) => {
  range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
    .backColor(COLORS.resultsBackground)
    .cellPadding(`0 ${FONTSIZE}`)
    .font(`${FONTSIZE}pt ${FONTNAME}`)
    // https://docs.microsoft.com/en-us/dotnet/standard/base-types/standard-numeric-format-strings
    .formatter('#,##0.0000')
    .textDecoration(null)
    .shrinkToFit();

  const { row, rowCount, col, colCount } = range;
  if (rowCount > 2) {
    for (let r = row; r < row + rowCount; r++) {
      if ((row - r) % 2 === 0) {
        const line = new GC.Spread.Sheets.CellRange(sheet, r, col, 1, colCount);
        line.backColor(COLORS.resultsLightBackground);
      }
    }
  }

  if (!toExport) {
    range.setBorder(new GC.Spread.Sheets.LineBorder(COLORS.subtleBorders, GC.Spread.Sheets.LineStyle.thin), {
      all: true,
      outline: false,
    });
  }

  return range;
};

export const setRangeToBeDeletedBackground = (range: GC.Spread.Sheets.CellRange, center = false) => {
  if (center) {
    range.hAlign(GC.Spread.Sheets.HorizontalAlign.center);
  }
  return range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .textDecoration(GC.Spread.Sheets.TextDecorationType.lineThrough)
    .font(`bold ${FONTSIZE}pt ${FONTNAME}`)
    .backColor('#D0D0D0')
    .foreColor('#404040');
};

export const setRangeDiagonals = (range: GC.Spread.Sheets.CellRange) =>
  range.diagonalUp(new GC.Spread.Sheets.LineBorder('#C0C0C0', GC.Spread.Sheets.LineStyle.thin));

export const setImmutableStyle = (range: GC.Spread.Sheets.CellRange) =>
  range.foreColor('rgb(64,64,64)').font(`italic ${FONTSIZE}pt ${FONTNAME}`);

export const setRangeToBeDeletedBackgroundHeader = (range: GC.Spread.Sheets.CellRange, center = false) => {
  if (center) {
    range.hAlign(GC.Spread.Sheets.HorizontalAlign.center);
  }
  return range
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .cellPadding(`0 ${FONTSIZE}`)
    .textDecoration(GC.Spread.Sheets.TextDecorationType.lineThrough)
    .backColor('#C0C0C0')
    .foreColor('#404040');
};

export const setWatermark = (range: GC.Spread.Sheets.CellRange, txt: string, alignRight = false) =>
  range
    .watermark(txt)
    .font(`${FONTSIZE}pt ${FONTNAME}`)
    .cellPadding(`0 ${FONTSIZE}`)
    .hAlign(alignRight ? GC.Spread.Sheets.HorizontalAlign.right : GC.Spread.Sheets.HorizontalAlign.left)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center);

/**
 * @description Column autofit with a minimum width of 45px for non-empty cells, 140px for empty cells to allow watermark display
 */
export const autoFitColumn = (sheet: GC.Spread.Sheets.Worksheet, col: number) => {
  if (!sheet) {
    return;
  }

  // const max = 140,
  const max = col === 0 ? 140 : 45;
  const min = 45;
  let minWidth = col === 0 ? max : min;

  try {
    sheet.autoFitColumn(col);
    const width = sheet.getCell(-1, col).width();
    if (col === 1) {
      const value = sheet.getCell(2, 1).value();
      minWidth = value === null || value === '' ? max : min;
    }

    if (width < minWidth) {
      sheet.getCell(-1, col).width(minWidth);
    }
  } catch (error) {
    // console.warn(`Error in autoFitColumn`, { error, name: sheet.name(), col });
  }

  // for (let r = 0; r < sheet.getRowCount(); r++) {
  //   try {
  //     sheet.autoFitRow(r);
  //   } catch (error) {
  //     // console.warn(`Error in autoFitColumn`, { error, name: sheet.name(), col });
  //   }
  // }
};

export const flashCell = (sheet: GC.Spread.Sheets.Worksheet, cell: GC.Spread.Sheets.CellRange, endValue = null) => {
  const wasPaintSuspended = sheet.isPaintSuspended();

  if (wasPaintSuspended) {
    sheet.resumePaint();
  }

  const originalColor = cell.backColor();
  let flash = true;

  let interval = setInterval(() => {
    cell.backColor(flash ? COLORS.flash : originalColor);
    flash = !flash;
  }, FLASH.interval);

  setTimeout(() => {
    clearInterval(interval);
    interval = null;
    cell.backColor(originalColor);
    cell.value(endValue);

    autoFitColumn(sheet, cell.col);
  }, FLASH.duration);

  if (wasPaintSuspended) {
    sheet.suspendPaint();
  }
};

export const center = (range: GC.Spread.Sheets.CellRange) => range.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

export const left = (range: GC.Spread.Sheets.CellRange) => range.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

export const right = (range: GC.Spread.Sheets.CellRange) => range.hAlign(GC.Spread.Sheets.HorizontalAlign.right);

export const drawTable = (
  infoTable: SpreadLookupInfo,
  sheet: GC.Spread.Sheets.Worksheet,
  structureCanBeEdited: boolean,
  userRoleCanEdit: boolean,
  toExport: boolean = false
) => {
  // console.log(`🎨 Drawing Table `, { infoTable: clone(infoTable), structureCanBeEdited, toExport });

  // sheet.options.allowCellOverflow = true;

  const { startRow, startCol, numberOfRows, numberOfColumns, toBeDeleted, toBeDeletedRows, toBeDeletedCols, dimMnemonics, references } =
    infoTable;
  const Dimensions = infoTable.references.length;

  // Is this a newly created or empty table?
  const isNewTable = Dimensions > 1 ? infoTable.rows.length === 0 || infoTable.cols.length === 0 : infoTable.rows.length === 0;

  // MNEMONIC
  const mnemonicCell = sheet.getCell(startRow, startCol);
  sheet.setRowHeight(mnemonicCell.row, 28, GC.Spread.Sheets.SheetArea.viewport);
  mnemonicCell.value(infoTable.mnemonic);
  mnemonicCell.locked(true);
  if (toBeDeleted && !isNewTable) {
    addGrayBorder(mnemonicCell);
    setRangeToBeDeletedBackgroundHeader(mnemonicCell, true);
  } else {
    addBlackBorder(mnemonicCell);
    setMnemonicBackground(mnemonicCell);
  }

  // SUBTITLE - N. of Dimensions cell
  const subtitleCell = sheet.getCell(startRow, startCol + 1);
  setWatermark(subtitleCell, `${Dimensions}D`, true);
  right(subtitleCell);
  subtitleCell.locked(true);

  // PIVOT
  const pivotCell = sheet.getCell(startRow + 1, startCol);
  sheet.setRowHeight(pivotCell.row, 25, GC.Spread.Sheets.SheetArea.viewport);
  pivotCell.value(infoTable.thirdDimension);
  if (toBeDeleted && !isNewTable) {
    addGrayBorder(pivotCell);
    setRangeToBeDeletedBackground(pivotCell, true);
  } else {
    if (structureCanBeEdited && Dimensions === 3) {
      pivotCell.locked(false);
      setWatermark(pivotCell, `Enter ${dimMnemonics[2]}`);
    }

    addBlackBorder(pivotCell);
    setThirdDimensionStyle(pivotCell);
  }

  // ROWS
  const rowsRange = sheet.getRange(startRow + 2, startCol, numberOfRows, 1);
  rowsRange.clear(GC.Spread.Sheets.StorageType.style);
  setColumnBackground(rowsRange, sheet, toExport);
  addBlackBorder(rowsRange);

  if (toBeDeleted && !isNewTable) {
    addGrayBorder(rowsRange);
    setRangeToBeDeletedBackgroundHeader(rowsRange);
    rowsRange.locked(true);
    const nextRowHeader = sheet.getCell(startRow + 2 + numberOfRows, 0);
    nextRowHeader.locked(true);
    setWatermark(nextRowHeader, `-`);
  } else if (structureCanBeEdited) {
    // addBlackBorder(rowsRange);
    rowsRange.locked(false);
    const nextRowHeader = sheet.getCell(startRow + 2 + numberOfRows, 0);
    nextRowHeader.locked(false);
    setWatermark(nextRowHeader, `Enter ${dimMnemonics[0]}`);
  }

  // RESULTS
  if (!isNewTable) {
    const resultsRange = sheet.getRange(startRow + 2, startCol + 1, numberOfRows, numberOfColumns);
    resultsRange.clear(GC.Spread.Sheets.StorageType.style);
    setResultsBackground(resultsRange, sheet, toExport);

    if (toBeDeleted) {
      addGrayBorder(resultsRange);
      setRangeToBeDeletedBackground(resultsRange);
      setRangeDiagonals(resultsRange);
    } else {
      // ! Fix 4336
      addBlackBorder(resultsRange);
    }

    if (!toBeDeleted && userRoleCanEdit) resultsRange.locked(false);
    else resultsRange.locked(true);
  }

  // COLUMNS
  if (references.length > 1) {
    const rangeCols = sheet.getRange(startRow + 1, startCol + 1, 1, numberOfColumns);
    rangeCols.clear(GC.Spread.Sheets.StorageType.style);
    setRowBackground(rangeCols, sheet, toExport);
    addBlackBorder(rangeCols);

    if (toBeDeleted && !isNewTable) {
      addGrayBorder(rangeCols);
      setRangeToBeDeletedBackgroundHeader(rangeCols);
      rangeCols.locked(true);
      const nextColHeader = sheet.getCell(startRow + 1, startCol + numberOfColumns + 1);
      nextColHeader.locked(true);
      setWatermark(nextColHeader, `-`);
    } else if (structureCanBeEdited) {
      // addBlackBorder(rangeCols);
      rangeCols.locked(false);
      const nextColHeader = sheet.getCell(startRow + 1, startCol + numberOfColumns + 1);
      nextColHeader.locked(false);
      setWatermark(nextColHeader, `Enter ${dimMnemonics[1]}`);
    }
  }

  // To be deleted Rows & Columns
  if (!toBeDeleted && !isNewTable) {
    if (toBeDeletedRows.length > 0) {
      toBeDeletedRows.forEach(deletedRow => {
        const rangeRowHeader = sheet.getRange(startRow + 2 + deletedRow, startCol, 1, 1);
        const rangeRowResults = sheet.getRange(startRow + 2 + deletedRow, startCol + 1, 1, numberOfColumns);
        rangeRowResults.locked(true);
        setRangeToBeDeletedBackgroundHeader(rangeRowHeader);
        setRangeToBeDeletedBackground(rangeRowResults);
        setRangeDiagonals(rangeRowResults);
      });
    }
    if (toBeDeletedCols.length > 0) {
      toBeDeletedCols.forEach(deletedCol => {
        const rangeColHeader = sheet.getRange(startRow + 1, startCol + 1 + deletedCol, 1, 1);
        const rangeColResults = sheet.getRange(startRow + 2, startCol + 1 + deletedCol, numberOfRows, 1);
        rangeColResults.locked(true);
        setRangeToBeDeletedBackgroundHeader(rangeColHeader);
        setRangeToBeDeletedBackground(rangeColResults);
        setRangeDiagonals(rangeColResults);
      });
    }
  }
};

/**
 *
 * @param rows Number of Rows the spreadsheet will have after clearing
 * @param columns Number of Columns the spreadsheet will have after clearing
 */
export const clearSheetStyles = (sheet: GC.Spread.Sheets.Worksheet, rows, columns) => {
  if (sheet) {
    try {
      const range = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
      sheet.setColumnCount(rows);
      sheet.setRowCount(columns);
      range.clear(GC.Spread.Sheets.StorageType.style);
      range.clear(GC.Spread.Sheets.StorageType.data);
    } catch {}
  }
};
export const clearRange = (range: GC.Spread.Sheets.CellRange) => {
  range.clear(GC.Spread.Sheets.StorageType.style);
  range.clear(GC.Spread.Sheets.StorageType.data);
};
